import { EventPageType } from '@graphql/generated-base';

import { useIsFeatureEnabled } from '@shared/core/featureFlags';

/**
 * Whether a page is the Wedding Map page.
 *
 * This compared the string `'map'` until 2026-10-29, because the enum member
 * ships from event_service (TRAV-5231) and `verify-gql-schema` validates joy-web
 * against the *live prod* schema — so naming it failed the build until that
 * service deployed. It reached prod on 2026-10-26 and joy-web has regenerated,
 * so this is the real enum now and the stand-in is gone.
 */
export const isMapPageType = (pageType: EventPageType): boolean => pageType === EventPageType.map;

/**
 * Whether the Wedding Map page may be shown to this guest at all.
 *
 * **Records no exposure.** This read only decides whether an entry is listed,
 * and it runs on every guest-site page view through `useGuestSitePages` — so
 * tracking it would count every guest of every event as exposed to
 * `guestWeddingMapRollout`, whether or not the event even has a map page, and
 * the experiment would measure nothing. The surfaces that actually show the
 * map — the Travel section and the `map` page itself — read the flag again
 * without this option, which is where the exposure belongs.
 *
 * While the flag resolves this reads false, so the page stays out of the list
 * until the guest is known to be in the rollout. Listing it and then removing
 * it would shift the nav under them.
 */
export const useIsMapPageVisible = (): boolean => {
  const [isEnabled] = useIsFeatureEnabled('guestWeddingMapRollout', { shouldTrackExposure: false });
  return isEnabled;
};

/**
 * Drops the Wedding Map page from a list unless the guest is in the rollout.
 *
 * Returns the **same array** when nothing is removed, which is every event
 * today. Several callers key effects on that reference — `useGuestSitePages`
 * and `useAlohaScroll` (LEGRSVP-85) — so a filter that always allocates hands
 * them a fresh array on every render and defeats the pin. Shared so the two
 * call sites cannot drift on that.
 */
export const withoutHiddenMapPage = <T extends { type: EventPageType }>(pages: ReadonlyArray<T>, isMapPageVisible: boolean): ReadonlyArray<T> => {
  if (isMapPageVisible) {
    return pages;
  }
  const next = pages.filter(page => !isMapPageType(page.type));
  return next.length === pages.length ? pages : next;
};
